import { useState, useEffect } from 'react'
import { authService } from '@/api/auth'
import {
  Button,
  Card,
  Input,
  Label,
  Textarea,
  Select,
  Tag,
  Alert,
  Icon,
  Title,
  Text,
} from '@nimbus-ds/components'
import {
  FlagIcon,
  PlusIcon,
  UndoIcon,
  DisketteIcon,
  EyeIcon,
  DragIcon,
  CloseIcon,
} from '@nimbus-ds/icons'
import type { FunnelStep, FunnelConfig, FunnelStepType } from '@/types'
import { useToast } from '@/hooks/use-toast'
import { funnelService } from '@/api/funnel'
import { useAuth } from '@/hooks/useAuth'

interface FunnelConfigurationProps {
  onConfigChange?: (config: FunnelConfig) => void
  initialConfig?: FunnelConfig
}

const defaultFunnelSteps: FunnelStep[] = [
  {
    id: 'step_1',
    name: 'Página Principal',
    type: 'pageview',
    description: 'Usuario visita la página principal de la tienda',
    trigger: {
      url_pattern: '/',
      match_type: 'exact',
      required: false,
    },
  },
  {
    id: 'step_2',
    name: 'PLP',
    type: 'pageview',
    description: 'Usuario navega por categorías, búsqueda o colecciones',
    trigger: {
      url_pattern: '/categoria/|/search/|/colecciones/',
      match_type: 'regex',
      required: false,
    },
  },
  {
    id: 'step_3',
    name: 'PDP',
    type: 'pageview',
    description: 'Usuario ve la página de un producto',
    trigger: {
      url_pattern: '/productos/',
      match_type: 'contains',
      required: false,
    },
  },
  {
    id: 'step_4',
    name: 'Agregar al Carrito',
    type: 'event',
    description: 'Usuario agrega producto al carrito',
    trigger: {
      event_name: 'add_to_cart',
      required: false,
    },
  },
  {
    id: 'step_5',
    name: 'Checkout',
    type: 'pageview',
    description: 'Usuario accede a la página de checkout',
    trigger: {
      url_pattern: '/checkout',
      match_type: 'contains',
      required: false,
    },
  },
  {
    id: 'step_6',
    name: 'Venta',
    type: 'conversion',
    description: 'Usuario completa la compra',
    trigger: {
      webhook_url: '/webhooks/order_paid',
      required: false,
    },
  },
]

const stepTypeOptions: {
  value: FunnelStepType
  label: string
  description: string
}[] = [
  {
    value: 'pageview',
    label: 'Vista de Página',
    description: 'Se activa cuando el usuario visita una URL específica',
  },
  {
    value: 'event',
    label: 'Evento',
    description: 'Se activa por un evento JavaScript específico',
  },
  {
    value: 'element_click',
    label: 'Click de Elemento',
    description: 'Se activa cuando se hace click en un elemento específico',
  },
  {
    value: 'element_visible',
    label: 'Elemento Visible',
    description: 'Se activa cuando un elemento se hace visible en pantalla',
  },
  {
    value: 'conversion',
    label: 'Conversión',
    description: 'Se activa por webhook o evento de conversión final',
  },
]

export function FunnelConfiguration({
  onConfigChange,
  initialConfig,
}: FunnelConfigurationProps) {
  const { toast } = useToast()
  const { user } = useAuth()
  const [config, setConfig] = useState<FunnelConfig>(
    initialConfig || {
      steps: defaultFunnelSteps,
      sessionTimeout: 30,
      attributionWindow: 7,
    }
  )

  const [draggedItem, setDraggedItem] = useState<number | null>(null)
  const [previewVisible, setPreviewVisible] = useState(false)

  // Load actual funnel config from API on mount
  useEffect(() => {
    if (initialConfig || !user?.store_id) return
    funnelService.getFunnelConfig(user.store_id)
      .then(apiConfig => {
        setConfig(apiConfig)
      })
      .catch(err => {
        console.warn('[FunnelConfig] Failed to load from API, using defaults:', err.message)
      })
  }, [user?.store_id])

  useEffect(() => {
    if (onConfigChange) {
      onConfigChange(config)
    }
  }, [config, onConfigChange])

  const addFunnelStep = () => {
    const newStep: FunnelStep = {
      id: `step_${Date.now()}`,
      name: 'Nuevo Paso',
      type: 'pageview',
      description: 'Descripción del paso',
      trigger: {
        url_pattern: '',
        required: true,
      },
    }

    setConfig((prev) => ({
      ...prev,
      steps: [...prev.steps, newStep],
    }))
  }

  const removeStep = (stepId: string) => {
    setConfig((prev) => ({
      ...prev,
      steps: prev.steps.filter((step) => step.id !== stepId),
    }))
  }

  const updateStep = (stepId: string, updates: Partial<FunnelStep>) => {
    setConfig((prev) => ({
      ...prev,
      steps: prev.steps.map((step) =>
        step.id === stepId ? { ...step, ...updates } : step
      ),
    }))
  }

  const resetToDefault = () => {
    setConfig({
      steps: defaultFunnelSteps,
      sessionTimeout: 30,
      attributionWindow: 7,
    })
  }

  const handleDragStart = (index: number) => {
    setDraggedItem(index)
  }

  const handleDragEnd = () => {
    setDraggedItem(null)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (draggedItem === null) return

    const newSteps = [...config.steps]
    const draggedStep = newSteps[draggedItem]
    newSteps.splice(draggedItem, 1)
    newSteps.splice(index, 0, draggedStep)

    setConfig((prev) => ({ ...prev, steps: newSteps }))
    setDraggedItem(index)
  }

  const saveConfiguration = async () => {
    try {
      // Validate that at least one conversion step exists
      const hasConversionStep = config.steps.some(step => step.type === 'conversion')
      if (!hasConversionStep) {
        toast({
          title: 'Error de validación',
          description: 'El funnel debe tener al menos un paso de tipo "conversión". Agrega un paso de conversión antes de guardar.',
          variant: 'destructive',
        })
        return
      }

      const storeId = authService.getCurrentStoreId()
      if (!storeId) {
        toast({
          title: 'Error',
          description: 'No se pudo obtener el ID de la tienda. Por favor, inicia sesión nuevamente.',
          variant: 'destructive',
        })
        return
      }

      const savedConfig = await funnelService.saveFunnelConfig({
        storeId,
        steps: config.steps,
        sessionTimeout: config.sessionTimeout,
        attributionWindow: config.attributionWindow,
      })

      // Update local state with saved configuration
      setConfig(savedConfig)

      toast({
        title: 'Configuración guardada',
        description: 'La configuración del funnel se ha guardado correctamente.',
      })
    } catch (error) {
      console.error('Failed to save funnel configuration:', error)
      toast({
        title: 'Error',
        description: 'No se pudo guardar la configuración del funnel.',
        variant: 'destructive',
      })
    }
  }

  const previewFunnelFlow = () => {
    setPreviewVisible(!previewVisible)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Icon source={<FlagIcon />} />
        <div>
          <Title as="h2">
            Configuración del Funnel de Conversión
          </Title>
          <Text>
            Define los pasos del funnel de conversión para tu tienda. Los
            experimentos podrán usar estos pasos como base.
          </Text>
        </div>
      </div>

      {/* Funnel Steps Configuration */}
      <Card>
        <Card.Header>
          <Title as="h3">Pasos del Funnel</Title>
          <Text>
            Arrastra y suelta para reordenar los pasos. El orden define la
            secuencia del funnel.
          </Text>
        </Card.Header>
        <Card.Body>
          <div className="space-y-4">
          {config.steps.map((step, index) => (
            <div
              key={step.id}
              className={`rounded-lg border bg-card p-4 transition-all ${
                draggedItem === index
                  ? 'scale-105 opacity-90 shadow-lg'
                  : 'hover:shadow-sm'
              }`}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
            >
              <div className="flex items-start gap-4">
                <div className="mt-2 flex items-center gap-2">
                  <Icon source={<DragIcon />} />
                  <Tag appearance="neutral">
                    {index + 1}
                  </Tag>
                </div>

                <div className="flex-1 space-y-3">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor={`step-name-${step.id}`}>
                        Nombre del Paso
                      </Label>
                      <Input
                        id={`step-name-${step.id}`}
                        value={step.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateStep(step.id, { name: e.target.value })
                        }
                        placeholder="ej. Vista de Producto"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`step-type-${step.id}`}>
                        Tipo de Paso
                      </Label>
                      <Select
                        id={`step-type-${step.id}`}
                        name={`step-type-${step.id}`}
                        value={step.type}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          updateStep(step.id, { type: e.target.value as FunnelStepType })
                        }
                      >
                        {stepTypeOptions.map((option) => (
                          <Select.Option
                            key={option.value}
                            label={option.label}
                            value={option.value}
                          />
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`step-desc-${step.id}`}>Descripción</Label>
                    <Textarea
                      id={`step-desc-${step.id}`}
                      value={step.description}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        updateStep(step.id, { description: e.target.value })
                      }
                      placeholder="Descripción de qué hace este paso"
                      lines={2}
                    />
                  </div>


                  {/* Trigger Configuration */}
                  <div className="space-y-2">
                    <Label>Configuración del Trigger</Label>
                    {step.type === 'pageview' && (
                      <div className="space-y-2">
                        <Label
                          htmlFor={`step-url-${step.id}`}
                        >
                          Patrón de URL
                        </Label>
                        <Input
                          id={`step-url-${step.id}`}
                          value={step.trigger.url_pattern || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateStep(step.id, {
                              trigger: {
                                ...step.trigger,
                                url_pattern: e.target.value,
                              },
                            })
                          }
                          placeholder="/products/*, /collections/*"
                        />
                      </div>
                    )}

                    {step.type === 'event' && (
                      <div className="space-y-2">
                        <Label
                          htmlFor={`step-event-${step.id}`}
                        >
                          Nombre del Evento
                        </Label>
                        <Input
                          id={`step-event-${step.id}`}
                          value={step.trigger.event_name || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateStep(step.id, {
                              trigger: {
                                ...step.trigger,
                                event_name: e.target.value,
                              },
                            })
                          }
                          placeholder="add_to_cart, click_cta"
                        />
                      </div>
                    )}

                    {step.type === 'element_click' && (
                      <div className="space-y-2">
                        <Label
                          htmlFor={`step-selector-${step.id}`}
                        >
                          Selector CSS
                        </Label>
                        <Input
                          id={`step-selector-${step.id}`}
                          value={step.trigger.selector || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateStep(step.id, {
                              trigger: {
                                ...step.trigger,
                                selector: e.target.value,
                              },
                            })
                          }
                          placeholder=".add-to-cart-btn, #checkout-button"
                        />
                      </div>
                    )}

                    {step.type === 'element_visible' && (
                      <div className="space-y-2">
                        <Label
                          htmlFor={`step-visible-selector-${step.id}`}
                        >
                          Selector CSS
                        </Label>
                        <Input
                          id={`step-visible-selector-${step.id}`}
                          value={step.trigger.selector || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateStep(step.id, {
                              trigger: {
                                ...step.trigger,
                                selector: e.target.value,
                              },
                            })
                          }
                          placeholder=".product-recommendations, .checkout-summary"
                        />
                      </div>
                    )}

                    {step.type === 'conversion' && (
                      <div className="space-y-2">
                        <Label
                          htmlFor={`step-webhook-${step.id}`}
                        >
                          Webhook URL
                        </Label>
                        <Input
                          id={`step-webhook-${step.id}`}
                          value={step.trigger.webhook_url || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateStep(step.id, {
                              trigger: {
                                ...step.trigger,
                                webhook_url: e.target.value,
                              },
                            })
                          }
                          placeholder="/webhooks/order_paid"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <Button
                  appearance="transparent"
                  onClick={() => removeStep(step.id)}
                  disabled={config.steps.length <= 1}
                >
                  <Icon source={<CloseIcon />} color="currentColor" />
                </Button>
              </div>
            </div>
          ))}

          <div className="flex gap-2">
            <Button onClick={addFunnelStep} appearance="neutral">
              <Icon source={<PlusIcon />} color="currentColor" />
              Agregar Paso
            </Button>
            <Button onClick={resetToDefault} appearance="neutral">
              <Icon source={<UndoIcon />} color="currentColor" />
              Restaurar por Defecto
            </Button>
          </div>
          </div>
        </Card.Body>
      </Card>

      {/* Advanced Configuration */}
      <Card>
        <Card.Header>
          <Title as="h3">Configuración Avanzada</Title>
        </Card.Header>
        <Card.Body>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="session-timeout">
                Tiempo de Sesión (minutos)
              </Label>
              <Input
                id="session-timeout"
                type="number"
                value={config.sessionTimeout}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setConfig((prev) => ({
                    ...prev,
                    sessionTimeout: parseInt(e.target.value) || 30,
                  }))
                }
                min="5"
                max="1440"
              />
              <Text fontSize="caption">
                Tiempo máximo entre pasos para considerarlos en la misma sesión
              </Text>
            </div>

            <div className="space-y-2">
              <Label htmlFor="attribution-window">
                Ventana de Atribución (días)
              </Label>
              <Input
                id="attribution-window"
                type="number"
                value={config.attributionWindow}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setConfig((prev) => ({
                    ...prev,
                    attributionWindow: parseInt(e.target.value) || 7,
                  }))
                }
                min="1"
                max="30"
              />
              <Text fontSize="caption">
                Tiempo máximo entre primer paso y conversión para atribuir la
                venta
              </Text>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Preview */}
      {previewVisible && (
        <Alert appearance="neutral" title="Vista Previa del Funnel">
          <div className="mt-2 flex items-center gap-2 overflow-x-auto pb-2">
            {config.steps.map((step, index) => (
              <div
                key={step.id}
                className="flex min-w-0 items-center gap-2"
              >
                <Tag appearance="neutral">
                  {index + 1}. {step.name}
                </Tag>
                {index < config.steps.length - 1 && (
                  <Text>→</Text>
                )}
              </div>
            ))}
          </div>
        </Alert>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button appearance="primary" onClick={saveConfiguration}>
          <Icon source={<DisketteIcon />} color="currentColor" />
          Guardar Configuración
        </Button>
        <Button onClick={previewFunnelFlow} appearance="neutral">
          <Icon source={<EyeIcon />} color="currentColor" />
          {previewVisible ? 'Ocultar' : 'Vista Previa del'} Flujo
        </Button>
      </div>
    </div>
  )
}
